/* eslint-disable */
import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
  WritableSignal,
  signal,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { FloatLabelType, MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged, filter, Subject, takeUntil, tap } from 'rxjs';
import { MatOption } from '@angular/material/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'app-universal-dropdown',
  templateUrl: './universal-dropdown.component.html',
  styleUrls: ['./universal-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => UniversalDropdownComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UniversalDropdownComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    MatSelectTrigger,
    NgTemplateOutlet,
    MatInput,
    MatCheckbox,
    MatOption,
    MatError,
    TranslatePipe,
  ],
})
export class UniversalDropdownComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  private controlContainer = inject(ControlContainer, { optional: true })!;
  private translate = inject(TranslateService);

  readonly disableOptionCentering = input(true);
  readonly isColumnFilterSearch = input(false);
  readonly multiple = input<boolean>();
  readonly selectAllOption = input(false);
  @Input() label: string;
  readonly placeholder = input<string>();
  readonly panelClass = input<string>();
  @Input() optionTestId: string | null = null;
  readonly floatLabel = input<FloatLabelType>('always');
  @Input() customPreview: TemplateRef<any>;
  @Input() customInputTrigger: TemplateRef<any>;
  readonly formControl = input<FormControl>(new FormControl([]));
  @Input() selectTestId: string | null = null;
  readonly formControlName = input<string>();
  readonly hasSearchBar = input(false);
  @Input() additionalTranslationString: string;
  readonly optionConditionsArray = input<any>();
  readonly dynamicSubscriptSizing = input(true);
  readonly isRequiredForce = input(false);
  @Input() errorTestId?: string;
  readonly isLoading = input<WritableSignal<boolean>>(signal(false));
  @Input() searchFunction: (searchStr: string, options: any[]) => any[] = this.defaultSearch;
  readonly valueSelected = output<void>();
  @ViewChild('matSelect', { static: true }) matSelect: MatSelect;
  public control: FormControl;
  public isRequired: boolean;
  public searchInput = '';
  public displayedOptions: any[] = [];
  // so that those options still appear in the mat-select-trigger
  public hiddenOptions: any[];
  protected isSelectAllSelected = false;
  private scrollTopBeforeSelection: number;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() set disabled(disabled: boolean) {
    if (disabled) {
      this.control?.disable();
    } else {
      this.control?.enable();
    }
  }

  private _options: any[];

  public get options(): any[] {
    return this._options;
  }

  @Input() set options(options: any[]) {
    if (JSON.stringify(this._options) !== JSON.stringify(options)) {
      this._options = options;
      this.resetDisplayedOptions();
    }
  }

  private _value: string;

  public get value(): string {
    return this._value;
  }

  @Input()
  set value(value: string) {
    this._value = value;
    if (this.control && value !== this.control.value) {
      this.control.patchValue(value);
    }
    this.onChange(value);
    this.onValidatorChange();
    this.onTouch(value);
  }

  readonly displayFn = input<(option: any) => string>(option => option);
  // We have to keep track of the hidden options, to create a hidden <mat-option>

  readonly valueFn = input<(option: any) => any>(option => option);

  readonly compareWith = input<(a: any, b: any) => boolean>((a, b) => a === b);

  public onChange: any = () => {};

  public onTouch: any = () => {};

  public onValidatorChange: any = () => {};

  public ngOnInit() {
    this.resetDisplayedOptions();
    this.setFormControl();
    this.setIsRequired();
    this.subscribeToControlChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public onSearch(searchInput: string): void {
    this.searchInput = searchInput;
    if (this.searchInput?.trim()?.length === 0) {
      this.resetDisplayedOptions();
      return;
    }

    this.displayedOptions = this.searchFunction(this.searchInput, this.options);
    this.hiddenOptions = this.options.filter(option =>
      this.isColumnFilterSearch()
        ? !this.displayedOptions.some(displayedOption => displayedOption.value === option.value)
        : !this.displayedOptions.includes(option),
    );
    this.updateSelectAllState();
  }

  public writeValue(value: string): void {
    this.value = value;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public setDisabledState(disabled: boolean) {
    if (disabled !== this.control.disabled) {
      this.disabled = disabled;
    }
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    /* control.value != 0 was added because if there was a dropdown with 0 as option the validation would not work on the first selection. E.g. account-info, accounting-area */
    if (!control.value && control.value != 0) {
      return control.errors;
    }
    return null;
  }

  public registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  protected toggleAllSelection(): void {
    const displayedValues = this.displayedOptions.map(option => this.valueFn()(option));

    this.isSelectAllSelected
      ? this.matSelect.options.forEach((item: MatOption) => {
          displayedValues.includes(item.value) && item.select();
        })
      : this.matSelect.options.forEach((item: MatOption) => {
          displayedValues.includes(item.value) && item.deselect();
        });
  }

  protected updateIsSelectAllSelected(isSelectAllSelected: boolean): void {
    this.isSelectAllSelected = isSelectAllSelected;
  }

  protected optionClick(): void {
    const displayedValues = this.displayedOptions.map(option => this.valueFn()(option));

    this.matSelect.options.forEach((item: MatOption) => {
      if (displayedValues.includes(item.value) && !item.selected) {
        this.isSelectAllSelected = false;
        return;
      }
    });
  }

  private updateSelectAllState(): void {
    if (this.multiple() && this.selectAllOption() && this.matSelect && this.matSelect.options && this.displayedOptions) {
      const displayedOptions = this.displayedOptions.map(option => this.valueFn()(option));
      const allDisplayedOptionsSelected = displayedOptions.every(value =>
        this.matSelect.options.some(option => option.value === value && option.selected),
      );

      if (allDisplayedOptionsSelected && !this.isSelectAllSelected) {
        this.isSelectAllSelected = true;
      } else if (!allDisplayedOptionsSelected && this.isSelectAllSelected) {
        this.isSelectAllSelected = false;
      }
    }
  }

  public resetDisplayedOptions(): void {
    this.displayedOptions = this.options;
    this.hiddenOptions = [];
    this.searchInput = '';
    this.updateSelectAllState();
  }

  private setFormControl(): void {
    this.control = (this.controlContainer?.control.get(this.formControlName()) as FormControl) || this.formControl();
  }

  private setIsRequired(): void {
    this.isRequired = this.control.hasValidator(Validators.required) || this.isRequiredForce();
  }

  private defaultSearch(searchStr: string, options: any[]): any[] {
    searchStr = searchStr.toLowerCase();
    return options.filter(option => !!option && this.translate.instant(this.displayFn()(option)).toLowerCase().includes(searchStr.trim()));
  }

  private subscribeToControlChanges(): void {
    this.control.valueChanges
      .pipe(distinctUntilChanged())
      .pipe(
        tap((value: string) => this.writeValue(value)),
        tap(() => this.updateSelectAllState()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
